import { useState, useEffect, useMemo, useCallback } from 'react';

const useVirtualGrid = (options) => {
  const scrollElement = options.scrollElement || document.body;
  const scrollArea = options.scrollArea;
  const [scrollYPosition, setScrollYPosition] = useState(0);
  const [scrollXPosition, setScrollXPosition] = useState(0);
  const { columnGap = 0, rowGap = 0, columnCount: columnCountProp = 1, itemWidth, itemHeight, itemsCount, scrollYMargin = 0, scrollXMargin = 0, overscan = 5 } = options;
  const columnCount = Math.max(columnCountProp, 1);
  const enabled = options.enabled ?? true;
  useEffect(() => {
    if (!scrollElement || !enabled) {
      return;
    }
    const updateScrollPosition = () => {
      setScrollYPosition(scrollElement.scrollTop);
      setScrollXPosition(scrollElement.scrollLeft);
    };
    scrollElement.addEventListener("scroll", updateScrollPosition);
    const resizeScrollElementObserver = new ResizeObserver(() => {
      updateScrollPosition();
    });
    resizeScrollElementObserver.observe(scrollElement);
    return () => {
      resizeScrollElementObserver.disconnect();
      scrollElement.removeEventListener("scroll", updateScrollPosition);
    };
  }, [
    scrollElement,
    enabled
  ]);
  const containerDimensions = useMemo(() => {
    const width = itemWidth * columnCount + (columnCount - 1) * columnGap;
    const totalRows = Math.ceil(itemsCount / columnCount);
    const height = itemHeight * totalRows + (totalRows - 1) * rowGap;
    return {
      width,
      height,
      totalRows
    };
  }, [
    itemWidth,
    itemHeight,
    columnCount,
    columnGap,
    rowGap,
    itemsCount
  ]);
  const getVirtualItems = useCallback(() => {
    if (!enabled) {
      return [];
    }
    const scrollAreaHeight = scrollArea?.offsetHeight || window.innerHeight;
    const scrollAreaWidth = scrollArea?.offsetWidth || window.innerWidth;
    const itemHeightWithGap = itemHeight + rowGap;
    const itemWidthWithGap = itemWidth + columnGap;
    const renderedRowsCount = Math.ceil(scrollAreaHeight / itemHeightWithGap) + 1;
    const renderedColumnsCount = Math.ceil(scrollAreaWidth / itemWidthWithGap) + 1;
    const topIndex = Math.max(Math.floor((scrollYPosition - scrollYMargin) / itemHeightWithGap), 0);
    const leftIndex = Math.max(Math.floor((scrollXPosition - scrollXMargin) / itemWidthWithGap), 0);
    const items = [];
    for (let i = -overscan; i < renderedRowsCount + overscan; i++) {
      for (let j = -overscan; j < renderedColumnsCount + overscan; j++) {
        const yIndex = topIndex + i;
        const xIndex = leftIndex + j;
        if (yIndex < 0 || yIndex >= containerDimensions.totalRows || xIndex < 0 || xIndex >= columnCount) {
          continue;
        }
        items.push({
          index: yIndex * columnCount + xIndex,
          x: j * itemWidthWithGap,
          y: i * itemHeightWithGap,
          row: yIndex,
          column: xIndex,
          width: itemWidth,
          height: itemHeight,
          startY: yIndex * itemHeightWithGap,
          startX: xIndex * itemWidthWithGap
        });
      }
    }
    return items;
  }, [
    scrollArea,
    scrollXPosition,
    scrollYPosition,
    itemHeight,
    itemWidth,
    columnCount,
    columnGap,
    rowGap,
    overscan,
    containerDimensions,
    scrollYMargin,
    scrollXMargin,
    enabled
  ]);
  return {
    containerDimensions,
    getVirtualItems,
    itemHeight,
    itemWidth
  };
};

export { useVirtualGrid };
//# sourceMappingURL=useVirtualGrid.js.map
